import _asyncToGenerator from "/builds/assist/zerobounce-website/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _classCallCheck from "/builds/assist/zerobounce-website/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/builds/assist/zerobounce-website/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _regeneratorRuntime from "/builds/assist/zerobounce-website/node_modules/@babel/runtime/regenerator/index.js";
/*!
 * Copyright (c) 2015-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * 
 * See the License for the specific language governing permissions and limitations under the License.
 */

import PCancelable from 'p-cancelable';
import AuthSdkError from '../errors/AuthSdkError.js';
import '../errors/WWWAuthError.js';
import { getConsole } from '../util/console.js';
import { PromiseQueue } from '../util/PromiseQueue.js';
import '../http/request.js';
import 'tiny-emitter';
import 'js-cookie';
import 'cross-fetch';
import '../oidc/types/Token.js';
import { EVENT_ADDED, EVENT_REMOVED } from '../oidc/types/TokenManager.js';
import '../_virtual/_tslib.js';
var INITIAL_AUTH_STATE = null;
var DEFAULT_PENDING = {
  updateAuthStatePromise: null,
  canceledTimes: 0
};
var EVENT_AUTH_STATE_CHANGE = 'authStateChange';
var MAX_PROMISE_CANCEL_TIMES = 10;
var isSameAuthState = function isSameAuthState(prevState, state) {
  if (!prevState) {
    return false;
  }
  return prevState.isAuthenticated === state.isAuthenticated && JSON.stringify(prevState.idToken) === JSON.stringify(state.idToken) && JSON.stringify(prevState.accessToken) === JSON.stringify(state.accessToken) && prevState.error === state.error;
};
var AuthStateManager = /*#__PURE__*/function () {
  function AuthStateManager(sdk) {
    var _this = this;
    _classCallCheck(this, AuthStateManager);
    if (!sdk.emitter) {
      throw new AuthSdkError('Emitter should be initialized before AuthStateManager');
    }
    this._sdk = sdk;
    this._pending = Object.assign({}, DEFAULT_PENDING);
    this._authState = INITIAL_AUTH_STATE;
    this._logOptions = {};
    this._prevAuthState = null;
    this._transformQueue = new PromiseQueue({
      quiet: true
    });
    sdk.tokenManager.on(EVENT_ADDED, function (key, token) {
      _this._setLogOptions({
        event: EVENT_ADDED,
        key: key,
        token: token
      });
      _this.updateAuthState();
    });
    sdk.tokenManager.on(EVENT_REMOVED, function (key, token) {
      _this._setLogOptions({
        event: EVENT_REMOVED,
        key: key,
        token: token
      });
      _this.updateAuthState();
    });
  }
  return _createClass(AuthStateManager, [{
    key: "_setLogOptions",
    value: function _setLogOptions(options) {
      this._logOptions = options;
    }
  }, {
    key: "getAuthState",
    value: function getAuthState() {
      return this._authState;
    }
  }, {
    key: "getPreviousAuthState",
    value: function getPreviousAuthState() {
      return this._prevAuthState;
    }
  }, {
    key: "updateAuthState",
    value: function () {
      var _updateAuthState = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _this2 = this;
        var _this$_sdk$options, transformAuthState, devMode, log, emitAuthStateChange, _finalPromise, cancelablePromise;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _this$_sdk$options = this._sdk.options, transformAuthState = _this$_sdk$options.transformAuthState, devMode = _this$_sdk$options.devMode;
              log = function log(status) {
                var _this2$_logOptions = _this2._logOptions,
                  event = _this2$_logOptions.event,
                  key = _this2$_logOptions.key,
                  token = _this2$_logOptions.token;
                getConsole().group("OKTA-AUTH-JS:updateAuthState: Event:".concat(event, " Status:").concat(status));
                getConsole().log(key, token);
                getConsole().log('Current authState', _this2._authState);
                getConsole().groupEnd();
                _this2._logOptions = {};
              };
              emitAuthStateChange = function emitAuthStateChange(authState) {
                if (isSameAuthState(_this2._authState, authState)) {
                  devMode && log('unchanged');
                  return;
                }
                _this2._prevAuthState = _this2._authState;
                _this2._authState = authState;
                _this2._sdk.emitter.emit(EVENT_AUTH_STATE_CHANGE, Object.assign({}, authState));
                devMode && log('emitted');
              };
              _finalPromise = function finalPromise(origPromise) {
                return _this2._pending.updateAuthStatePromise.then(function () {
                  var curPromise = _this2._pending.updateAuthStatePromise;
                  if (curPromise && curPromise !== origPromise) {
                    return _finalPromise(curPromise);
                  }
                  return _this2.getAuthState();
                });
              };
              if (!this._pending.updateAuthStatePromise) {
                _context.next = 11;
                break;
              }
              if (!(this._pending.canceledTimes >= MAX_PROMISE_CANCEL_TIMES)) {
                _context.next = 10;
                break;
              }
              devMode && log('terminated');
              return _context.abrupt("return", _finalPromise(this._pending.updateAuthStatePromise));
            case 10:
              this._pending.updateAuthStatePromise.cancel();
            case 11:
              cancelablePromise = new PCancelable(function (resolve, _, onCancel) {
                onCancel.shouldReject = false;
                onCancel(function () {
                  _this2._pending.updateAuthStatePromise = null;
                  _this2._pending.canceledTimes = _this2._pending.canceledTimes + 1;
                  devMode && log('canceled');
                });
                var emitAndResolve = function emitAndResolve(authState) {
                  if (cancelablePromise.isCanceled) {
                    resolve();
                    return;
                  }
                  emitAuthStateChange(authState);
                  resolve();
                  _this2._pending = Object.assign({}, DEFAULT_PENDING);
                };
                _this2._sdk.isAuthenticated().then(function () {
                  if (cancelablePromise.isCanceled) {
                    resolve();
                    return;
                  }
                  var _this2$_sdk$tokenMana = _this2._sdk.tokenManager.getTokensSync(),
                    accessToken = _this2$_sdk$tokenMana.accessToken,
                    idToken = _this2$_sdk$tokenMana.idToken,
                    refreshToken = _this2$_sdk$tokenMana.refreshToken;
                  var authState = {
                    accessToken: accessToken,
                    idToken: idToken,
                    refreshToken: refreshToken,
                    isAuthenticated: !!(accessToken && idToken)
                  };
                  var promise = transformAuthState ? _this2._transformQueue.push(transformAuthState, null, _this2._sdk, authState) : Promise.resolve(authState);
                  promise.then(function (authState) {
                    return emitAndResolve(authState);
                  }).catch(function (error) {
                    return emitAndResolve({
                      accessToken: accessToken,
                      idToken: idToken,
                      refreshToken: refreshToken,
                      isAuthenticated: false,
                      error: error
                    });
                  });
                });
              });
              this._pending.updateAuthStatePromise = cancelablePromise;
              return _context.abrupt("return", _finalPromise(cancelablePromise));
            case 14:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function updateAuthState() {
        return _updateAuthState.apply(this, arguments);
      }
      return updateAuthState;
    }()
  }, {
    key: "subscribe",
    value: function subscribe(handler) {
      this._sdk.emitter.on(EVENT_AUTH_STATE_CHANGE, handler);
    }
  }, {
    key: "unsubscribe",
    value: function unsubscribe(handler) {
      this._sdk.emitter.off(EVENT_AUTH_STATE_CHANGE, handler);
    }
  }]);
}();
export { AuthStateManager, INITIAL_AUTH_STATE };